import type { FlightExplorerRequest, FlightSearchParams } from './search_source.js';

/**
 * Helpers PUROS sobre o pedido do explorer (`FlightExplorerRequest`): geometria
 * da viagem, params de busca por perna e a re-forma pela faixa. Não dependem do
 * estado — vivem fora de `explorer_state.ts` porque aquele arquivo está no teto
 * de linhas, e a API pública continua a mesma (re-exportados de lá).
 */

/** A GEOMETRIA da viagem do explorer — forma, pernas e volta, e nada mais
 *  (cabine e sujeito da política são do PEDIDO, não da viagem; o irmão
 *  server-side desta régua é o `tripGeometryKey` dos agents). É o vocabulário do
 *  comparador ÚNICO: o aviso da ShellBar, o re-rótulo da casca e o galho do adopt
 *  do card multi olham exatamente estes campos — dois comparadores divergiriam
 *  no primeiro campo novo e a tela avisaria uma coisa e despacharia outra. */
export type ExplorerTripGeometry = Pick<FlightExplorerRequest, 'mode' | 'legs' | 'returnDate'>;

export function tripGeometryOf(request: FlightExplorerRequest): ExplorerTripGeometry {
  return {
    mode: request.mode,
    legs: request.legs.map((l) => ({
      origin: l.origin,
      destination: l.destination,
      departDate: l.departDate,
    })),
    ...(request.mode === 'round_trip' && request.returnDate
      ? { returnDate: request.returnDate }
      : {}),
  };
}

/** Divergiu = forma OU pernas (rota+data, posicional) OU a volta do round_trip.
 *  `returnDate` fora do RT não pesa: é ruído de pedido antigo que
 *  `editedRequest`/`search_started` já não re-formam. */
export function sameTripGeometry(a: ExplorerTripGeometry, b: ExplorerTripGeometry): boolean {
  if (a.mode !== b.mode || a.legs.length !== b.legs.length) return false;
  const legsEqual = a.legs.every((leg, i) => {
    const other = b.legs[i];
    return (
      leg.origin === other?.origin &&
      leg.destination === other.destination &&
      leg.departDate === other.departDate
    );
  });
  if (!legsEqual) return false;
  return a.mode !== 'round_trip' || a.returnDate === b.returnDate;
}

/** Params da busca de UMA perna: RT leva `returnDate` no passo 1 (o searchKey
 *  nasce round-trip); multi é one-way por contrato. Cabine e sujeito da
 *  política valem pro pedido inteiro. */
export function legSearchParams(request: FlightExplorerRequest, leg: number): FlightSearchParams {
  const l = request.legs[leg];
  return {
    origin: l?.origin ?? '',
    destination: l?.destination ?? '',
    departureDate: l?.departDate ?? '',
    ...(request.mode === 'round_trip' && request.returnDate
      ? { returnDate: request.returnDate }
      : {}),
    ...(request.cabin ? { cabin: request.cabin } : {}),
    ...(request.policyTravelerId ? { policyTravelerId: request.policyTravelerId } : {}),
    // Linhagem: a perna buscada amarrada ao card multi que a originou.
    ...(request.itineraryChallengeId
      ? { itinerary: { challengeId: request.itineraryChallengeId, legIndex: leg } }
      : {}),
  };
}

/**
 * O pedido que um roteiro RE-EDITADO na faixa produz.
 *
 * Exportado (e não embutido no handler) porque o espelho de IO do componente
 * precisa dos params de CADA perna no mesmo turno em que despacha o evento — o
 * commit do reducer só chega no render seguinte, e derivar a mesma coisa duas
 * vezes é como a busca de uma perna sairia com a cabine que o pedido não tem mais.
 *
 * O modo segue a FORMA: um roteiro reduzido a um trecho é uma viagem de ida, e
 * mantê-lo `multi_leg` deixaria um stepper de um passo só descrevendo uma viagem
 * que não tem trechos. `returnDate` NÃO sobrevive — cada trecho de um roteiro é
 * one-way por contrato, e a volta de um pedido binário anterior faria a perna 0
 * buscar um PAR que seria adotado como perna. Cabine e sujeito da política, ao
 * contrário, são do PEDIDO e não do roteiro: quem os move é a coluna de cortes, e
 * re-formar o roteiro não pode desfazer o corte dela. A linhagem do card
 * (`itineraryChallengeId`) também fica: re-editar a rota de um trecho ainda é o
 * MESMO card multi.
 */
export function editedRequest(
  request: FlightExplorerRequest,
  legs: FlightExplorerRequest['legs'],
): FlightExplorerRequest {
  return {
    mode: legs.length > 1 ? 'multi_leg' : 'one_way',
    legs: [...legs],
    ...(request.cabin ? { cabin: request.cabin } : {}),
    ...(request.policyTravelerId ? { policyTravelerId: request.policyTravelerId } : {}),
    ...(request.travelerCount ? { travelerCount: request.travelerCount } : {}),
    ...(request.itineraryChallengeId ? { itineraryChallengeId: request.itineraryChallengeId } : {}),
  };
}
